import type { Input } from './sim';
import { SIM_VERSION } from './version';

/**
 * リプレイは映像ではなく「シード + フレームごとの入力」で保存する。
 *
 * これ 1 つで
 *   - ゴースト（前回の自分・世界 1 位との並走）
 *   - サーバー側での不正検知（上位のリプレイを再生してスコアを検証する）
 *   - 共有用の動画書き出し
 * のすべてが実現でき、しかも通信量は 1 走行あたり数キロバイトに収まる。
 *
 * 後から足せない設計なので Phase 0 から入れている。
 */

/** 横入力は 1 バイトに量子化する。量子化幅を変えると過去のリプレイと非互換になる。 */
const LATERAL_STEPS = 8;

export interface Replay {
  /** 記録形式のバージョン。バイト並びを変えたら上げる。 */
  readonly version: number;
  /**
   * シミュレーションの挙動バージョン。**形式とは別物。**
   * 同じバイト列でも挙動が違えば再生結果が変わるので、両方を持つ必要がある。
   */
  readonly simVersion: number;
  readonly stageId: string;
  readonly seed: number;
  /** 1 フレーム 1 バイト。下位 4 ビットが横入力、最上位ビットがダイブ。 */
  readonly inputs: Int8Array;
}

export const REPLAY_VERSION = 1;

function quantize(lateral: number): number {
  const clamped = Math.max(-1, Math.min(1, lateral));
  return Math.round(clamped * LATERAL_STEPS);
}

function dequantize(step: number): number {
  return step / LATERAL_STEPS;
}

/** 生の入力を、記録できる粒度に丸めた正規形。 */
export function canonicalInput(input: Input): Input {
  return { lateral: dequantize(quantize(input.lateral)), dive: input.dive };
}

/**
 * 走行中に入力を積んでいく。
 *
 * record は「実際にシミュレーションへ渡すべき入力」を返す。呼び出し側が生の入力で
 * シミュレーションを進めてしまうと、記録は量子化されているためリプレイが本走行と
 * ずれる。戻り値を使うことで、記録と実行が一致することを構造で保証する。
 */
export class ReplayRecorder {
  private readonly frames: number[] = [];
  private readonly stageId: string;
  private readonly seed: number;

  constructor(stageId: string, seed: number) {
    this.stageId = stageId;
    this.seed = seed;
  }

  record(input: Input): Input {
    const lateral = quantize(input.lateral);
    // -8..8 を 0..16 にずらし、ダイブを 32 のビットで持つ。Int8 の範囲に収める。
    this.frames.push(lateral + LATERAL_STEPS + (input.dive ? 32 : 0));
    return { lateral: dequantize(lateral), dive: input.dive };
  }

  get frameCount(): number {
    return this.frames.length;
  }

  finish(): Replay {
    return {
      version: REPLAY_VERSION,
      simVersion: SIM_VERSION,
      stageId: this.stageId,
      seed: this.seed,
      inputs: Int8Array.from(this.frames),
    };
  }
}

/** 保存済みのリプレイを 1 フレームずつ再生する。ゴーストの駆動に使う。 */
export class ReplayPlayer {
  private cursor = 0;
  private readonly replay: Replay;

  constructor(replay: Replay) {
    this.replay = replay;
  }

  get exhausted(): boolean {
    return this.cursor >= this.replay.inputs.length;
  }

  next(): Input {
    if (this.exhausted) return { lateral: 0, dive: false };
    const byte = this.replay.inputs[this.cursor++];
    const dive = (byte & 32) !== 0;
    const lateral = dequantize((byte & 31) - LATERAL_STEPS);
    return { lateral, dive };
  }

  reset(): void {
    this.cursor = 0;
  }
}

/** localStorage / ネットワーク越しに運ぶための JSON 表現。 */
export interface SerializedReplay {
  readonly version: number;
  readonly simVersion: number;
  readonly stageId: string;
  readonly seed: number;
  readonly inputs: string;
}

export function serializeReplay(replay: Replay): SerializedReplay {
  let binary = '';
  for (let i = 0; i < replay.inputs.length; i++) {
    binary += String.fromCharCode(replay.inputs[i] & 0xff);
  }
  return {
    version: replay.version,
    simVersion: replay.simVersion,
    stageId: replay.stageId,
    seed: replay.seed,
    inputs: btoa(binary),
  };
}

export function deserializeReplay(data: SerializedReplay): Replay {
  const binary = atob(data.inputs);
  const inputs = new Int8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    inputs[i] = (binary.charCodeAt(i) << 24) >> 24;
  }
  return {
    version: data.version,
    simVersion: data.simVersion ?? 1,
    stageId: data.stageId,
    seed: data.seed,
    inputs,
  };
}
